import { useEffect } from 'react';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  robots?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogImageWidth?: string;
  ogImageHeight?: string;
  ogImageAlt?: string;
  ogUrl?: string;
  ogType?: string;
  ogLocale?: string;
  ogSiteName?: string;
  twitterCard?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  twitterImageAlt?: string;
  twitterUrl?: string;
  geoPosition?: string;
  geoRegion?: string;
  geoPlacename?: string;
  jsonLd?: object;
}

export const useSEO = ({
  title,
  description,
  keywords,
  canonical,
  robots = 'index, follow',
  ogTitle,
  ogDescription,
  ogImage,
  ogImageWidth,
  ogImageHeight,
  ogImageAlt,
  ogUrl,
  ogType = 'website',
  ogLocale = 'de_DE',
  ogSiteName = 'Bamme-Media',
  twitterCard = 'summary_large_image',
  twitterTitle,
  twitterDescription,
  twitterImage,
  twitterImageAlt,
  twitterUrl,
  geoPosition,
  geoRegion,
  geoPlacename,
  jsonLd
}: SEOProps) => {
  useEffect(() => {
    // Title
    if (title) {
      document.title = title;
    }

    // Meta tags
    const updateMetaTag = (name: string, content: string, property?: boolean) => {
      const selector = property ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let meta = document.querySelector(selector) as HTMLMetaElement;
      
      if (!meta) {
        meta = document.createElement('meta');
        if (property) {
          meta.setAttribute('property', name);
        } else {
          meta.setAttribute('name', name);
        }
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    // Basic meta tags
    if (description) updateMetaTag('description', description);
    if (keywords) updateMetaTag('keywords', keywords);
    if (robots) updateMetaTag('robots', robots);

    // Geo tags for local SEO
    if (geoPosition) updateMetaTag('geo.position', geoPosition);
    if (geoRegion) updateMetaTag('geo.region', geoRegion);
    if (geoPlacename) updateMetaTag('geo.placename', geoPlacename);
    if (geoPosition) updateMetaTag('ICBM', geoPosition.replace(';', ', '));

    // Open Graph tags
    if (ogTitle) updateMetaTag('og:title', ogTitle, true);
    if (ogDescription) updateMetaTag('og:description', ogDescription, true);
    if (ogImage) updateMetaTag('og:image', ogImage, true);
    if (ogImageWidth) updateMetaTag('og:image:width', ogImageWidth, true);
    if (ogImageHeight) updateMetaTag('og:image:height', ogImageHeight, true);
    if (ogImageAlt) updateMetaTag('og:image:alt', ogImageAlt, true);
    if (ogUrl) updateMetaTag('og:url', ogUrl, true);
    if (ogType) updateMetaTag('og:type', ogType, true);
    if (ogLocale) updateMetaTag('og:locale', ogLocale, true);
    if (ogSiteName) updateMetaTag('og:site_name', ogSiteName, true);

    // Twitter tags
    if (twitterCard) updateMetaTag('twitter:card', twitterCard, true);
    if (twitterTitle) updateMetaTag('twitter:title', twitterTitle, true);
    if (twitterDescription) updateMetaTag('twitter:description', twitterDescription, true);
    if (twitterImage) updateMetaTag('twitter:image', twitterImage, true);
    if (twitterImageAlt) updateMetaTag('twitter:image:alt', twitterImageAlt, true);
    if (twitterUrl) updateMetaTag('twitter:url', twitterUrl, true);

    // Canonical link
    if (canonical) {
      let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      link.setAttribute('href', canonical);
    }

    // Last modified
    const today = new Date().toISOString().split('T')[0];
    updateMetaTag('last-modified', today);

    // JSON-LD structured data
    if (jsonLd) {
      // Remove all existing JSON-LD scripts
      document.querySelectorAll('script[type="application/ld+json"]').forEach(s => s.remove());

      const items = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      items.forEach((item) => {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.textContent = JSON.stringify(item);
        document.head.appendChild(script);
      });
    }
  }, [
    title, description, keywords, canonical, robots,
    ogTitle, ogDescription, ogImage, ogImageWidth, ogImageHeight, ogImageAlt, ogUrl, ogType, ogLocale, ogSiteName,
    twitterCard, twitterTitle, twitterDescription, twitterImage, twitterImageAlt, twitterUrl,
    geoPosition, geoRegion, geoPlacename, jsonLd
  ]);
};
